import { ChangeDetectionStrategy, Component, DestroyRef, Signal } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { RootState } from '@root/app.state';
import { selectSharepointTemplateExtraction, selectTemplateActualisation } from '@core/core.selectors';
import { filterSucceededResource, ResourceStatuses } from '@platform/shared/resource.state';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { TemplateActualisation } from '@core/core.state';
import { map } from 'rxjs/operators';
import * as CoreActions from '@core/core.actions';
import { pendingFlag, withPendingFlag } from '@platform/shared/utilities/pending-flag';
import { formatDate } from '@angular/common';
import { ReportService } from '@core/report/report.service';
import { saveAs } from 'file-saver';
import { ToastService } from '@platform/toast/toast.service';
import moment from 'moment';
import { FormBuilder, Validators } from '@angular/forms';
import { FileUploadDialogComponent } from '@platform/components/file-upload-widget/file-upload/file-upload-dialog/file-upload-dialog.component';
import { DialogV2Service } from '@platform/dialog/dialog-v2.service';
import { PortalConfig } from '@platform/platform.state';
import { selectPortalConfig } from '@platform/platform.selectors';

@Component({
  selector: 'app-admin',
  templateUrl: './admin.component.html',
  styleUrls: ['./admin.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminComponent {
  public readonly latestTemplateUpdates: Signal<TemplateActualisation | undefined>;

  public readonly extractPending: Signal<boolean>;

  public readonly customerSpaceReportPending = pendingFlag();
  public readonly projectReportPending = pendingFlag();
  public readonly userReportPending = pendingFlag();
  public readonly chargingReportPending = pendingFlag();
  public readonly fileProcessReportPending = pendingFlag();
  public readonly portalConfig: Signal<PortalConfig | null | undefined>;

  public readonly chargingReportForm = this.formBuilder.group({
    periodStartDate: [moment().subtract(1, 'months').startOf('month'), [Validators.required]],
    periodEndDate: [moment().subtract(1, 'months').endOf('month'), [Validators.required]],
  });

  constructor(
    private store$: Store<RootState>,
    private destroyRef: DestroyRef,
    private reportService: ReportService,
    private toastService: ToastService,
    private formBuilder: FormBuilder,
    private dialogService: DialogV2Service,
  ) {
    this.latestTemplateUpdates = toSignal(store$.pipe(select(selectTemplateActualisation), filterSucceededResource()));

    this.extractPending = toSignal(
      store$.pipe(
        select(selectSharepointTemplateExtraction),
        map((state) => state.status === ResourceStatuses.PENDING),
      ),
      { initialValue: false },
    );

    this.portalConfig = toSignal(store$.pipe(select(selectPortalConfig)));
  }

  public extractCustomerSpaceTemplate() {
    this.store$.dispatch(CoreActions.extractSharepointCustomerSpaceTemplate());
  }

  public extractProjectTemplate() {
    this.store$.dispatch(CoreActions.extractSharepointProjectTemplate());
  }

  public downloadCustomerSpaceReport() {
    this.reportService
      .getCustomerSpaceReport()
      .pipe(withPendingFlag(this.customerSpaceReportPending))
      .subscribe({
        next: (response) => saveAs(response, `Berechtigungsreport_MYO2_Kundenraum_${formatDate(new Date(), 'yyyyMMdd', 'en')}.xlsx`),
        error: () => this.toastService.showErrorToast('admin.reports.error-message'),
        complete: () => this.toastService.showSuccessToast('admin.reports.success-message'),
      });
  }

  public downloadProjectReport() {
    this.reportService
      .getProjectReport()
      .pipe(withPendingFlag(this.projectReportPending))
      .subscribe({
        next: (response) => saveAs(response, `Berechtigungsreport_MYO2_Projekt_${formatDate(new Date(), 'yyyyMMdd', 'en')}.xlsx`),
        error: () => this.toastService.showErrorToast('admin.reports.error-message'),
        complete: () => this.toastService.showSuccessToast('admin.reports.success-message'),
      });
  }

  public downloadUserReport() {
    this.reportService
      .getUserReport()
      .pipe(withPendingFlag(this.userReportPending))
      .subscribe({
        next: (response) => saveAs(response, `Berechtigungsreport_MYO2_Benutzer_${formatDate(new Date(), 'yyyyMMdd', 'en')}.xlsx`),
        error: () => this.toastService.showErrorToast('admin.reports.error-message'),
        complete: () => this.toastService.showSuccessToast('admin.reports.success-message'),
      });
  }

  public downloadChargingReport() {
    if (!this.chargingReportForm.valid) {
      return;
    }

    this.reportService
      .getChargingReport(this.chargingReportForm.value.periodStartDate!, this.chargingReportForm.value.periodEndDate!)
      .pipe(withPendingFlag(this.chargingReportPending))
      .subscribe({
        next: (response) => saveAs(response, `Charging_Report_MYO2_${formatDate(new Date(), 'yyyyMMdd', 'en')}.xlsx`),
        error: () => this.toastService.showErrorToast('admin.reports.error-message'),
        complete: () => this.toastService.showSuccessToast('admin.reports.success-message'),
      });
  }

  public downloadFileProcessReport() {
    this.reportService
      .getFileProcessReport()
      .pipe(withPendingFlag(this.fileProcessReportPending))
      .subscribe({
        next: (response) => saveAs(response, `File_Process_Report_MYO2_${formatDate(new Date(), 'yyyyMMdd', 'en')}.xlsx`),
        error: () => this.toastService.showErrorToast('admin.reports.error-message'),
        complete: () => this.toastService.showSuccessToast('admin.reports.success-message'),
      });
  }

  openFileUploadDialog() {
    const portalConfig = this.portalConfig();
    if (!portalConfig) {
      return;
    }

    this.dialogService
      .open(FileUploadDialogComponent, {
        data: {
          multiple: false,
          invalidCharacters: portalConfig.invalidFileAndFolderNameCharacters,
          allowedFileFormats: ['xlsx'],
        },
      })
      .afterClosed()
      .subscribe((value) => {
        if (value) {
          this.toastService.showPendingToast('upload-success-message');
          this.store$.dispatch(
            CoreActions.uploadBatchCreationFile({
              files: value.files,
            }),
          );
        }
      });
  }

  public uploadBatchCreateCustomerSpacesFiles(event: Event) {
    if (event.target && 'files' in event.target && event.target.files instanceof FileList) {
      // Upload file.
    }
  }
}
